import {
  Box,
  VStack,
  HStack,
  Text,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverHeader,
  PopoverBody,
  VisuallyHidden,
  Button,
  Divider,
} from "@chakra-ui/react";
import { CurrencyText } from "../../../shared/ui";
import { Plus, Star, CalendarPlus } from "../../../shared/icons";
import { formatBRL } from "../../../shared/lib/currency";
import { vinculoColorToken } from "../../vinculos/utils/vinculoColors";
import { EVENT_KIND_META, eventLabel } from "../../calendarEvents/utils/calendarEventKinds";
import { describeShiftTime } from "../utils/shiftTime";
import ShiftRow from "./ShiftRow";
import CalendarEventRow from "./CalendarEventRow";

// Faixa neutra dos compromissos do dia (folga, férias...): ícone do tipo e, a partir do
// md, o nome do primeiro + quantos outros. Cor neutra de propósito — compromisso não é
// valor nem status financeiro.
function CalendarEventStrip({ events }) {
  const first = events[0];
  const Icon = EVENT_KIND_META[first.tipo]?.icon;
  const label = events.map(eventLabel).join(", ");
  return (
    <HStack spacing={1} mt={1} px={1} py={0.5} borderRadius="sm" bg="app-muted" color="app-fg" minW={0} title={label}>
      {Icon ? <Icon size={10} aria-hidden /> : null}
      <Text fontSize="10px" isTruncated minW={0} display={{ base: "none", md: "block" }}>
        {eventLabel(first)}
        {events.length > 1 ? ` +${events.length - 1}` : ""}
      </Text>
      <VisuallyHidden>Compromisso: {label}</VisuallyHidden>
    </HStack>
  );
}

// Linha do título nativo da célula: "Hospital São Lucas · 19:00 → 07:00 (+1) · troca com a Dra. Ana".
function describeShiftForTitle(shift) {
  return [shift.vinculo_nome, describeShiftTime(shift), shift.observacao].filter(Boolean).join(" · ");
}

// Nome acessível do dia que abre popover: "Ver dia 5: plantão realizado R$ 1.200,00;
// compromisso Férias" — o mesmo resumo que a célula mostra visualmente.
function describeDayForLabel(day, events) {
  const parts = [
    day.plantao_realizado > 0 ? `plantão realizado ${formatBRL(day.plantao_realizado)}` : "",
    day.pagamento_previsto > 0 ? `pagamento previsto ${formatBRL(day.pagamento_previsto)}` : "",
    events.length > 0 ? `compromisso ${events.map(eventLabel).join(", ")}` : "",
  ].filter(Boolean);
  return `Ver dia ${day.dia}: ${parts.join("; ")}`;
}

// Célula de um dia do calendário. Qualquer dia com evento (plantão realizado ou pagamento
// previsto) vira gatilho de um Popover: no topo, o resumo dos valores do dia — necessário
// porque em telas estreitas a célula só mostra pontos —, e abaixo os plantões trabalhados
// naquele dia (`shifts`, agrupados por DataTrabalho — não pela previsão de pagamento), com
// edição, reagendamento, cancelamento e transições do fluxo financeiro. Dia sem evento
// vira atalho para registrar um plantão já com a data preenchida (`onRegister`).
// Plantão importante ganha uma estrela discreta; hora e observação aparecem no título
// (tooltip nativo) da célula e no popover. `vinculosById` (id -> vínculo) traz a cor do
// vínculo — marcador ao lado do nome, nunca no valor, que segue a cor de status — e o
// endereço do "Como chegar" no menu de ações do plantão.
// Compromissos (`events`: folga, férias...) aparecem numa faixa neutra na célula e no
// popover, com editar/excluir; um dia só com compromisso também abre o popover, que traz
// "Adicionar compromisso" com a data do dia (`onAddEvent`).
export default function CalendarDayCell({
  day,
  shifts,
  onEdit,
  onReschedule,
  onCancel,
  onInvoiceIssued,
  onReceived,
  onRegister,
  onDuplicate,
  // compromissos que ocupam este dia (já filtrados pela página) e suas ações
  events = [],
  onEditEvent,
  onDeleteEvent,
  onAddEvent,
  vinculosById = {},
}) {
  const hasEvent = day.plantao_realizado > 0 || day.pagamento_previsto > 0;
  const hasCalendarEvents = events.length > 0;
  // dia com plantão/pagamento ou com compromisso abre o popover; só o dia vazio registra direto
  const opensPopover = hasEvent || hasCalendarEvents;
  const canRegister = Boolean(onRegister);
  const vinculoNames = [...new Set(shifts.map((shift) => shift.vinculo_nome))];
  const firstVinculo = shifts.length > 0 ? vinculosById[shifts[0].vinculo_id] : undefined;
  const hasImportant = shifts.some((shift) => shift.importante);
  const cellTitle = shifts.map(describeShiftForTitle).join("\n");
  // A célula é um Box, não um <button>: Enter e Espaço precisam ser tratados à mão.
  const activateWith = (action) => (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      action(event);
    }
  };
  // Dia vazio é só um atalho de registro: vira botão acessível em vez de abrir popover.
  // Dia com evento (inclusive só compromisso) vira o gatilho do popover, também focável;
  // o PopoverTrigger só liga o clique, então o teclado repassa para ele.
  const interactiveProps = opensPopover
    ? {
        role: "button",
        tabIndex: 0,
        "aria-label": describeDayForLabel(day, events),
        onKeyDown: activateWith((event) => event.currentTarget.click()),
        _focusVisible: { boxShadow: "outline" },
      }
    : canRegister
      ? {
          role: "button",
          tabIndex: 0,
          "aria-label": `Registrar plantão no dia ${day.dia}`,
          onClick: onRegister,
          onKeyDown: activateWith(() => onRegister()),
          _focusVisible: { boxShadow: "outline" },
        }
      : {};

  const content = (
    <Box
      minH={{ base: "52px", md: "84px" }}
      border="1px solid"
      borderColor={day.hoje ? "primary" : "app-border"}
      borderWidth={day.hoje ? "2px" : "1px"}
      borderRadius="md"
      p={{ base: 1, md: 1.5 }}
      bg={day.hoje ? "accent-subtle" : opensPopover ? "app-card" : "transparent"}
      cursor={opensPopover || canRegister ? "pointer" : "default"}
      _hover={opensPopover || canRegister ? { borderColor: "primary" } : undefined}
      data-group
      {...interactiveProps}
    >
      <HStack justify="space-between" spacing={1}>
        <HStack spacing={0.5}>
          <Text fontSize="xs" fontWeight={day.hoje ? "bold" : "medium"} color={day.hoje ? "accent-text" : "app-muted-fg"}>
            {day.dia}
          </Text>
          {hasImportant ? (
            <Box as="span" color="warning.500" display="inline-flex" role="img" aria-label="Plantão importante" title={cellTitle}>
              <Star size={10} fill="currentColor" aria-hidden />
            </Box>
          ) : null}
        </HStack>
        {!opensPopover && canRegister ? (
          <Box color="primary" opacity={0} _groupHover={{ opacity: 1 }} display={{ base: "none", md: "block" }} aria-hidden>
            <Plus size={12} />
          </Box>
        ) : null}
      </HStack>
      {/* Em telas estreitas a célula tem ~40px de largura e o valor em reais viraria só
          "R$…"; ali mostramos os pontos da legenda e os valores ficam no resumo do popover. */}
      <HStack spacing={1} mt={1} display={{ base: "flex", md: "none" }}>
        {day.plantao_realizado > 0 ? (
          <>
            <Box boxSize="8px" borderRadius="full" bg="primary" />
            <VisuallyHidden>Plantão realizado: {formatBRL(day.plantao_realizado)}</VisuallyHidden>
          </>
        ) : null}
        {day.pagamento_previsto > 0 ? (
          <>
            <Box boxSize="8px" borderRadius="full" bg="teal.400" />
            <VisuallyHidden>Pagamento previsto: {formatBRL(day.pagamento_previsto)}</VisuallyHidden>
          </>
        ) : null}
      </HStack>
      <VStack align="stretch" spacing={0.5} mt={1} display={{ base: "none", md: "flex" }}>
        {day.plantao_realizado > 0 ? (
          <Text fontSize="10px" color="accent-text" fontWeight="semibold" noOfLines={1}>
            {formatBRL(day.plantao_realizado)}
          </Text>
        ) : null}
        {day.pagamento_previsto > 0 ? (
          <Text fontSize="10px" color="teal-text" fontWeight="semibold" noOfLines={1}>
            ↓ {formatBRL(day.pagamento_previsto)}
          </Text>
        ) : null}
        {/* Onde é o plantão: com mais de um vínculo no dia, o primeiro nome + quantos
            outros; a lista completa fica no popover. */}
        {vinculoNames.length > 0 ? (
          <HStack spacing={1} minW={0}>
            {firstVinculo?.cor ? (
              <Box
                flexShrink={0}
                w="3px"
                h="10px"
                borderRadius="full"
                bg={vinculoColorToken(firstVinculo.cor)}
                aria-hidden
                data-testid="vinculo-color-bar"
                data-cor={firstVinculo.cor}
              />
            ) : null}
            <Text fontSize="10px" color="app-muted-fg" noOfLines={1} title={cellTitle}>
              {vinculoNames[0]}
              {vinculoNames.length > 1 ? ` +${vinculoNames.length - 1}` : ""}
            </Text>
          </HStack>
        ) : null}
      </VStack>
      {hasCalendarEvents ? <CalendarEventStrip events={events} /> : null}
    </Box>
  );

  if (!opensPopover) {
    return content;
  }

  return (
    <Popover
      placement="bottom"
      isLazy
      modifiers={[{ name: "preventOverflow", options: { padding: 16 } }]}
    >
      <PopoverTrigger>{content}</PopoverTrigger>
      <PopoverContent w={{ base: "calc(100vw - 32px)", sm: "300px" }}>
        <PopoverArrow />
        <PopoverCloseButton />
        <PopoverHeader fontWeight="semibold" fontSize="sm">
          Dia {day.dia}
          {shifts.length > 0 ? ` · ${shifts.length} plantão(ões)` : ""}
        </PopoverHeader>
        <PopoverBody>
          <VStack align="stretch" spacing={3}>
            <VStack align="stretch" spacing={0.5}>
              {day.plantao_realizado > 0 ? (
                <Text fontSize="sm" color="accent-text">
                  Plantão realizado: <CurrencyText value={day.plantao_realizado} />
                </Text>
              ) : null}
              {day.pagamento_previsto > 0 ? (
                <Text fontSize="sm" color="teal-text">
                  Pagamento previsto: <CurrencyText value={day.pagamento_previsto} />
                </Text>
              ) : null}
            </VStack>
            {shifts.map((shift) => (
              <ShiftRow
                key={shift.id}
                shift={shift}
                vinculo={vinculosById[shift.vinculo_id]}
                actions={{ onEdit, onReschedule, onCancel, onInvoiceIssued, onReceived, onDuplicate }}
              />
            ))}
            {events.map((event) => (
              <CalendarEventRow key={`compromisso-${event.id}`} event={event} onEdit={onEditEvent} onDelete={onDeleteEvent} />
            ))}
            {canRegister || onAddEvent ? <Divider /> : null}
            {canRegister ? (
              <Button size="sm" variant="outline" leftIcon={<Plus size={14} />} onClick={onRegister}>
                Registrar plantão neste dia
              </Button>
            ) : null}
            {onAddEvent ? (
              <Button size="sm" variant="ghost" leftIcon={<CalendarPlus size={14} />} onClick={onAddEvent}>
                Adicionar compromisso
              </Button>
            ) : null}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
